import { Grid, IconButton, Paper } from '@material-ui/core'
import {
  InsertDriveFile,
  PersonAdd,
  Print,
  Visibility,
} from '@material-ui/icons'
import { Download } from '@mui/icons-material'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getProjectDetails } from '../../../actions/projectActions'
import {
  PROJECT_ADD_CONTRIB_RESET,
  PROJECT_REMOVE_CONTRIB_RESET,
} from '../../../constants/projectConstants'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'
import ToastMessage from '../../ToastMessage/ToastMessage'
import ContributorAdd from './ContributorAdd'
import Contributors from './Contributors'
import PrintDownloadLoaderView from './PrintDownloadLoaderView'
import ProjectAddress from './ProjectAddress'
import RequestSummary from './RequestSummary'

const ProjectInfo = () => {
  const dispatch = useDispatch()

  const printRef = useRef()

  const { project } = useSelector((state) => state.projectDetails)
  const { success: addContribSuccess } = useSelector(
    (state) => state.projectAddContributors
  )

  const { success: removeContribSuccess } = useSelector(
    (state) => state.projectRemoveContributors
  )

  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [showAddContributorModal, setShowAddContributorModal] = useState(false)
  const [showAddContributorToast, setShowAddContributorToast] = useState(false)
  const [showContributorModal, setShowContributorModal] = useState(false)
  const [showRemoveContributorToast, setShowRemoveContributorToast] =
    useState(false)
  const [summaryIsReady, setSummaryIsReady] = useState(false)
  const [downloadDoc, setDownloadDoc] = useState(false)
  const [printDoc, setPrintDoc] = useState(false)

  useEffect(() => {
    const downloadSummary = async () => {
      try {
        const margin = 10
        const element = printRef.current
        const canvas = await html2canvas(element)
        const imgData = canvas.toDataURL('image/png')
        const doc = new jsPDF('p', 'mm')
        const imgWidth = 210
        const pageHeight = 295
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        let position = 0
        let heightLeft = imgHeight

        doc.addImage(
          imgData,
          'PNG',
          margin,
          margin,
          imgWidth - margin * 2,
          imgHeight - margin * 2
        )

        heightLeft -= pageHeight - margin

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight
          doc.addPage()
          doc.addImage(
            imgData,
            'PNG',
            margin,
            position,
            imgWidth - margin * 2,
            imgHeight - margin * 2
          )
          heightLeft -= pageHeight
        }

        if (downloadDoc) {
          doc.save(`${project.name}.pdf`)
        } else {
          // print
          window.open(doc.output('bloburl'))
        }

        setSummaryIsReady(false)
        setShowSummaryModal(false)
        setDownloadDoc(false)
        setPrintDoc(false)
      } catch (error) {
        console.log(error)
      }
    }

    if ((downloadDoc || printDoc) && summaryIsReady) {
      downloadSummary()
    }
  }, [downloadDoc, printDoc, project.name, showSummaryModal, summaryIsReady])

  useEffect(() => {
    if (addContribSuccess) {
      setShowAddContributorModal(false)
      setShowAddContributorToast(true)
      dispatch({ type: PROJECT_ADD_CONTRIB_RESET })
      dispatch(getProjectDetails(project._id))
    }

    if (removeContribSuccess) {
      setShowRemoveContributorToast(true)
      dispatch({ type: PROJECT_REMOVE_CONTRIB_RESET })
      dispatch(getProjectDetails(project._id))
    }
  }, [addContribSuccess, removeContribSuccess, dispatch, project])

  const handleDownloadSummary = async () => {
    setShowSummaryModal(true)
    setDownloadDoc(true)
  }

  const handlePrint = () => {
    setShowSummaryModal(true)
    setPrintDoc(true)
  }

  return (
    <>
      <div className="flex space-between">
        <h2>{project.name}</h2>
        <div className="flex">
          <div
            className="name-dots flex hide-580 cursor"
            onClick={() => setShowContributorModal(true)}
          >
            {project.contributorData &&
              // eslint-disable-next-line array-callback-return
              project.contributorData.map((x, index) => {
                const length = project.contributorData.length

                if (length >= 3 && index === 0) {
                  return (
                    <div key={x._id} className="name-dot gray outlined">
                      +{length}
                    </div>
                  )
                } else if (length < 3) {
                  return (
                    <div key={x._id} className="name-dot gray">
                      {x.firstName.charAt(0)}
                      {x.lastName.charAt(0)}
                    </div>
                  )
                }
              })}

            {project.assigneeInitials && (
              <div className="name-dot gray">{project.assigneeInitials}</div>
            )}
            <div className="name-dot gray">{project.userInitials}</div>
          </div>
          <IconButton
            className="btn-dashed m-l-16"
            onClick={() => setShowAddContributorModal(true)}
          >
            <PersonAdd />
          </IconButton>
        </div>
      </div>
      <div className="fs14 flex">
        <div className="strong m-r-10" style={{ width: 50 }}>
          Service
        </div>
        <div className="gray-text">Contract {project.type}</div>
      </div>
      <div className="fs14 flex">
        <div className="strong m-r-10" style={{ width: 50 }}>
          Client
        </div>
        <div className="gray-text">{project.userName}</div>
      </div>

      <ProjectAddress />

      <Grid container spacing={4}>
        <Grid item lg={6} md={6} sm={6} xs={12}>
          <Paper
            className="m-t-50 p-t-5 p-b-5 p-r-5 p-l-10"
            style={{ borderRadius: 0 }}
          >
            <div className="flex space-between fs12 align-center">
              <div
                className="primary flex align-center"
                onClick={() => setShowSummaryModal(true)}
                style={{ cursor: 'pointer' }}
              >
                <InsertDriveFile className="m-r-5" style={{ fontSize: 16 }} />
                Request summary
              </div>

              <div>
                <IconButton
                  className="small"
                  onClick={() => setShowSummaryModal(true)}
                >
                  <Visibility style={{ color: 'black' }} />
                </IconButton>

                <IconButton
                  sx={{ marginLeft: 6 }}
                  className="small"
                  onClick={handlePrint}
                >
                  <Print style={{ fontSize: 16, color: 'black' }} />
                </IconButton>

                <IconButton
                  sx={{ marginLeft: 6 }}
                  className="small"
                  onClick={handleDownloadSummary}
                >
                  <Download style={{ fontSize: 16, color: 'black' }} />
                </IconButton>
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>

      <ModalPopUp
        title="Request summary"
        variant="full tall"
        setShowModal={setShowSummaryModal}
        showModal={showSummaryModal}
      >
        {(downloadDoc || printDoc) && <PrintDownloadLoaderView />}
        <RequestSummary innerRef={printRef} setReady={setSummaryIsReady} />
      </ModalPopUp>

      <ModalPopUp
        title="Add contributors"
        setShowModal={setShowAddContributorModal}
        showModal={showAddContributorModal}
        variant="large"
      >
        <ContributorAdd
          setShowAddContributorModal={setShowAddContributorModal}
        />
      </ModalPopUp>

      <ModalPopUp
        title="Project contributors"
        setShowModal={setShowContributorModal}
        showModal={showContributorModal}
        variant="medium"
      >
        <Contributors setShowContributorModal={setShowContributorModal} />
      </ModalPopUp>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Contributor(s) invited</p>
            <p className="fs14 m-b-0">
              Invitations to join {project.name} were succesfully sent.
            </p>
          </>
        }
        open={showAddContributorToast}
        setOpen={setShowAddContributorToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Contributor removed</p>
            <p className="fs14 m-b-0">
              Contributor successfully removed from project.
            </p>
          </>
        }
        open={showRemoveContributorToast}
        setOpen={setShowRemoveContributorToast}
      />
    </>
  )
}

export default ProjectInfo
